/**
 * 爱微圈导航 · 缓存 / 埋点 / 通知 / 同步
 * 本文件是运行时源（唯一真源）—— 由 tools/split_appjs.py 从 assets/js/app.js 一次性
 * 拆分而来，此后各模块独立维护。**改前端逻辑就改这里**，改完执行 `bash build.sh`
 * 打包成 assets/js/app.min.js（index.php 自动优先引用 .min 产物）。
 * assets/js/app.js 只是拆分时的原始素材，运行时不再加载，勿改。
 */

import { applyWeather, greeting, loadDailyContent, loadWeather, renderClockGreet, renderWeatherPop, tickClock, toggleWeatherPop, weatherGeolocate } from './clock.js';
import { $, $$, API, API_TIMEOUT_MS, CACHE_KEY_LEGACY, DEFAULT_SETTINGS, apiGet, apiPost, bootData, cacheKey, debounce, esc, escAttr, fetchWithTimeout, randomColor, safeUrl, shouldAnimate, state, toast } from './core.js';
import { CAT_ICONS, ICO_CHECK, ICO_COPY, ICO_EDIT, ICO_EXPAND, ICO_PIN, ICO_TRASH, NOTE_COLORS, addNote, addTodo, copyNote, loadCategories, loadNotes, loadTodos, loadWebsiteIcon, loadWebsites, observeWebsiteIcons, openNoteEditor, preloadAdjacentCategories, relTime, renderCategories, renderNotes, renderTodos, renderWebsiteList, startTodoEdit, toggleTodo, websiteIconUrls } from './data.js';
import { showContextMenu, showEditModal, showFolderModal } from './edit.js';
import { BUILTIN_ACTIONS, init } from './main.js';
import { closeModal, createCloudBackup, loadBackupListInto, modalConfirm, openModal, runDataRepair, setupDataButtons, showBackupList, showModalError, showQrSync } from './modal.js';
import { ONBOARDING_STEPS, closeOnboarding, maybeShowOnboarding, nextOnbStep, renderOnbStep, showOnboarding } from './onboarding.js';
import { batchDelete, batchMoveToPage, enforcePageCapacity, getPageCapacity, goNextPage, goPrevPage, initBatchToolbar, loadShortcuts, loadShortcutsRaw, prefetchAdjacentPages, prefetchPage, renderPages, shiftPagesAfterRemoval, switchPage, toggleOrganizeMode, updateBatchToolbar, updatePageArrows } from './pages.js';
import { _readIconStore, _reduceMotion, _saveIconStore, _scFp, cardRenderFp, currentGrid, faviconOf, flipMove, getGridCols, gridRenderFp, persistGridReorder, preconnectUrl, renderCardView, renderCardViewAll, renderIcon, renderShortcuts, renderSkeleton, waitTransition } from './render.js';
import { TAB_TARGETS, bindEngineIconFallback, currentEngine, engineBadgeHtml, engineFamily, fetchSuggestions, handleSearch, hideSuggestions, openTarget, renderEngineBadge, renderEngineDropdown, showEngineAddModal } from './search.js';
import { applySettings, bindSettingsUI, loadSettings, scheduleSave } from './settings.js';
import { bindDonateCard, bindShareCard, detectPayApp, fmtDonateTime, loadDonateRank, renderDonateRank, shareQrSrc, shareTargetUrl, shareTextDesc, shareTextTitle, showAboutModal, showContactModal, showDonateModal, showFeedbackModal, showPrivacyModal, showShareModal, showTermsModal } from './share.js';
import { closeDrawers, closeSidebar, doLogout, formatTime, loadUserCategories, openDrawer, openSidebar, renderProfile, resetDataView, showAccountModal, showAuthModal, showCategoryMgr, showCustomAddModal, showDeleteAccountModal, showEmailChangeModal, showForgotPasswordModal, showPhoneBindModal, showQuickAddModal, showSyncChoiceModal, switchSidebarTab } from './sidebar.js';
import { SWATCH_COLORS, showWallpaperModal } from './wallpaper.js';

export const CACHE_TTL = 7 * 24 * 60 * 60 * 1000;

let _cacheTimer = null;

export function cacheState() {
    clearTimeout(_cacheTimer);
    _cacheTimer = setTimeout(() => {
        try {
            localStorage.setItem(cacheKey(), JSON.stringify({
                ts: Date.now(),
                settings: state.settings,
                shortcuts: state.shortcuts,
                pages: state.pages,
                allCategories: state.allCategories || null,
                bingWallpaper: state.bingWallpaper || null,
            }));
        } catch (e) { /* ignore */ }
    }, 500);
}

export function restoreCache() {
    try { localStorage.removeItem(CACHE_KEY_LEGACY); } catch (e) { /* 忽略 */ }
    try {
        const raw = localStorage.getItem(cacheKey());
        if (!raw) return false;
        const c = JSON.parse(raw);
        if (c.ts && Date.now() - c.ts > CACHE_TTL) return false;
        if (c.settings) state.settings = { ...DEFAULT_SETTINGS, ...c.settings };
        if (c.bingWallpaper) state.bingWallpaper = c.bingWallpaper;
        if (c.shortcuts) {
            state.shortcuts = c.shortcuts;
            state.shortcutCache[state.currentPage] = c.shortcuts;
        }
        if (c.pages) state.pages = c.pages;
        if (c.allCategories) state.allCategories = c.allCategories;
        return true;
    } catch (e) { return false; }
}

/* ---------- 搜索 Tab UI ---------- */

export function applySearchTabUI() {
    $$('.search-tab').forEach(t => t.classList.toggle('active', t.dataset.tab === state.searchTab));
}

/* ---------- 初始化 ---------- */

function getVid() {
    let vid = '';
    try { vid = localStorage.getItem('awq_vid') || ''; } catch (e) {}
    if (!vid) {
        vid = ('vid' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12)).slice(0, 32);
        try { localStorage.setItem('awq_vid', vid); } catch (e) {}
    }
    return vid;
}

export function trackVisit() {
    try {
        const vid = getVid();
        const p = location.pathname || '/';
        const r = document.referrer || '';
        const send = () => {
            try {
                const q = '?vid=' + encodeURIComponent(vid) + '&p=' + encodeURIComponent(p) + '&r=' + encodeURIComponent(r);
                fetch(API.visit + q, { method: 'GET', credentials: 'same-origin', keepalive: true }).catch(() => {});
            } catch (e) {}
        };
        if (window.requestIdleCallback) requestIdleCallback(send, { timeout: 3000 });
        else setTimeout(send, 2000);
    } catch (e) {}
}

/* 点击埋点：记录用户点击某个快捷方式/站点（供后台「数据分析 → 点击最多网站」）。
 * 用 sendBeacon 优先（导航跳转时仍能发出），降级 fetch keepalive。失败静默，不影响跳转。 */
export function trackClick(url, name) {
    try {
        if (!url) return;
        const vid = getVid();
        const q = '?vid=' + encodeURIComponent(vid) + '&url=' + encodeURIComponent(url) + '&name=' + encodeURIComponent(name || '');
        const target = API.click + q;
        const sent = navigator.sendBeacon && navigator.sendBeacon(target);
        if (!sent) {
            try { fetch(target, { method: 'GET', credentials: 'same-origin', keepalive: true }).catch(() => {}); } catch (e) {}
        }
    } catch (e) {}
}


export function notifyEnabled() {
    try { return localStorage.getItem('notif_enabled') === '1'; } catch(e) { return false; }
}

export function setNotifyEnabled(v) {
    try { localStorage.setItem('notif_enabled', v ? '1' : '0'); } catch(e) {}
}

export async function enableNotifications() {
    if (!('Notification' in window)) {
        toast('当前浏览器不支持通知', 'error');
        return false;
    }
    if (Notification.permission === 'granted') {
        setNotifyEnabled(true);
        toast(window.i18n ? window.i18n.t('notif.enabled') : '通知已开启', 'success');
        return true;
    }
    if (Notification.permission === 'denied') {
        toast(window.i18n ? window.i18n.t('notif.permission_denied') : '通知权限被拒绝', 'error');
        return false;
    }
    const perm = await Notification.requestPermission();
    if (perm === 'granted') {
        setNotifyEnabled(true);
        new Notification('爱微圈导航', { body: '通知已开启，我们会及时提醒你重要事项' });
        toast(window.i18n ? window.i18n.t('notif.enabled') : '通知已开启', 'success');
        return true;
    }
    toast(window.i18n ? window.i18n.t('notif.permission_denied') : '通知权限被拒绝', 'error');
    return false;
}

export function disableNotifications() {
    setNotifyEnabled(false);
    toast(window.i18n ? window.i18n.t('notif.disabled') : '通知已关闭', 'success');
}

export function showNotification(title, options) {
    if (!notifyEnabled() || !('Notification' in window) || Notification.permission !== 'granted') return;
    try { new Notification(title, options || {}); } catch(e) {}
}

/* ==================== 云同步开关 ==================== */

export function syncEnabled() {
    // 服务端权威值在 users.sync_enabled（public_user 注入 state.user）；
    // settings/__INITIAL__ 为兼容旧路径的兜底
    const u = state.user && state.user.sync_enabled;
    if (u !== undefined && u !== null) return !!Number(u);
    const s = (state.settings && state.settings.sync_enabled) ||
              (window.__INITIAL__ && window.__INITIAL__.settings && window.__INITIAL__.settings.sync_enabled);
    return s !== '0' && s !== 0;
}

export async function toggleSync(enabled) {
    if (!state.user) { toast('请先登录', 'error'); return; }
    try {
        const data = await apiPost(API.auth + '?action=toggle_sync', { enabled: !!enabled });
        if (data && data.user) state.user = data.user;
        if (state.user) state.user.sync_enabled = enabled ? 1 : 0;
        if (state.settings) state.settings.sync_enabled = enabled ? '1' : '0';
        toast(enabled ? (window.i18n ? window.i18n.t('sync.enabled') : '云同步已开启')
                      : (window.i18n ? window.i18n.t('sync.disabled') : '云同步已关闭'), 'success');
        if (enabled) {
            showNotification('云同步', { body: '数据已自动同步到云端' });
        }
    } catch(e) {
        toast('同步设置失败：' + e.message, 'error');
    }
}

/* ==================== 搜索分类-引擎映射 ==================== */

export const CATEGORY_ENGINE_MAP = {
    'web': '',
    'images': 'images',
    'news': 'news',
    'videos': 'videos',
    'books': 'books',
    'academic': 'academic'
};

export function mapCategoryToEngine(category) {
    const mapping = CATEGORY_ENGINE_MAP[category];
    if (mapping && state.engines) {
        const found = state.engines.find(e => e.type === mapping || e.name.toLowerCase().includes(mapping));
        if (found) return found;
    }
    return null;
}

/* ==================== 用户分享网站 ==================== */

export async function submitSiteForReview(data) {
    if (!state.user) { toast('请先登录后再分享', 'error'); return false; }
    try {
        await apiPost(API.websites + '?action=submit_site', {
            name: data.name,
            url: data.url,
            description: data.description || '',
            category: data.category || '用户分享',
            icon_url: data.icon_url || '',
            icon_color: data.icon_color || '#5B8DD4',
            icon_text: data.icon_text || '',
            icon_type: data.icon_url ? 'url' : 'color'
        });
        toast('网站已提交，等待管理员审核', 'success');
        return true;
    } catch(e) {
        toast('提交失败：' + e.message, 'error');
        return false;
    }
}

